import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/site/LanguageToggle";
import { getExhibitorToken } from "@/lib/apiClient";
import { useSitePhase } from "@/lib/useSitePhase";

export const Header = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const isLoggedIn = !!getExhibitorToken();
  const phase = useSitePhase();
  const betweenEvents = !!phase?.between_events;

  const links = [
    { to: "/tickets", key: "tickets" },
    { to: "/booths", key: "booths" },
    { to: "/hallenplan", key: "hallenplan" },
    { to: "/aussteller", key: "exhibitors" },
    { to: "/faq", key: "faq" },
    { to: "/kontakt", key: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2.5 shrink-0"
        >
          <img
            src="/brand/ccc-pictorial.svg"
            alt="CCC"
            className="h-9 w-auto"
          />
          <img
            src="/brand/ccc-lettermark.svg"
            alt="Cologne Card Con"
            className="h-7 w-auto"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-6 font-body text-sm text-zinc-300">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className="hover:text-white transition-colors whitespace-nowrap"
            >
              {t(`nav.${l.key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <LanguageToggle />
          <Link
            to={isLoggedIn ? "/account" : "/account/login"}
            data-testid="header-account-link"
            className="hidden xl:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-300 hover:text-white border border-white/15 rounded-full px-3 py-1.5 hover:border-white/30 transition-colors whitespace-nowrap"
          >
            <User size={13} /> {isLoggedIn ? "Mein Konto" : "Aussteller-Login"}
          </Link>
          <Link
            to={betweenEvents ? "/booths#aussteller-vormerken" : "/tickets"}
            data-testid="header-cta"
            className="hidden xl:inline-flex gold-button text-sm whitespace-nowrap"
          >
            {betweenEvents ? "Vormerken" : t("nav.cta")}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden text-white p-1"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 bg-black/90 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.key}`}
              onClick={() => setOpen(false)}
              className="text-zinc-200 font-body"
            >
              {t(`nav.${l.key}`)}
            </Link>
          ))}
          <Link
            to={isLoggedIn ? "/account" : "/account/login"}
            data-testid="mobile-account-link"
            onClick={() => setOpen(false)}
            className="text-zinc-200 font-body inline-flex items-center gap-1.5"
          >
            <User size={14} /> {isLoggedIn ? "Mein Konto" : "Aussteller-Login"}
          </Link>
          <Link
            to={betweenEvents ? "/booths#aussteller-vormerken" : "/tickets"}
            onClick={() => setOpen(false)}
            className="gold-button text-sm w-fit"
          >
            {betweenEvents ? "Vormerken" : t("nav.cta")}
          </Link>
        </div>
      )}
    </header>
  );
};
